import { CallEmsApi, iEmsApiResponseBody, networkFailure } from "../Logic/TenantAPI";

export type MailDirection = "Inbound" | "Outbound";
export type MailOrigin = "AgentReply" | "AutoResponse";

export type ProcessingStatus =
    "Queued" | "Processing" | "Completed" | "Failed" | "DeadLettered" | "Building" | "Sendable" | "Sent" | "Abandoned";

/**
 * The drill-down contract (contract §2.7).
 *
 * `id` means `dataLinkID` for inbound and `messageID` for outbound. There is
 * no synthetic identifier, and the two occupy wildly different ranges — 9776
 * against 100192461 in the dev tenant — so a consumer that picked an id field
 * per direction would look correct on one and be wrong on the other. Carry
 * `ref` as a unit.
 */
export interface EmailRef {
    direction: MailDirection;
    id: number;
}

interface EmailListItemBase {
    ref: EmailRef;
    mailboxID?: number;
    /** Nullable on the wire. A row can arrive without a status. */
    status?: ProcessingStatus;
    /** Server-computed at read time. Never re-derive it (contract §2.3). */
    stalled: boolean;
    /**
     * Absent for a row that has not been processed far enough to link a
     * message. An empty subject is a real state, not missing data — and it is
     * exactly the row an operator wants, since it failed early.
     */
    subject?: string;
    from?: string;
    to?: string;
    processingUpdatedAt?: string;
    lastFailureReason?: string;
}

export interface InboundEmailListItem extends EmailListItemBase {
    receivedAt?: string;
    hasAttachments: boolean;
}

export interface OutboundEmailListItem extends EmailListItemBase {
    origin?: MailOrigin;
    queuedAt?: string;
    sentAt?: string;
    attemptCount?: number;
}

export type EmailListItem = InboundEmailListItem | OutboundEmailListItem;

/**
 * Narrows the row union.
 *
 * `ref.direction` is a NESTED discriminant, and TypeScript 4.9 narrows unions
 * only on a direct property. A plain `item.ref.direction === "Inbound"` check
 * therefore compiles but narrows nothing, leaving every inbound-only field
 * unreachable. This guard is what makes the union usable.
 */
export function isInbound(item: EmailListItem): item is InboundEmailListItem {
    return item.ref.direction === "Inbound";
}

export interface Paged<TItem> {
    /**
     * Optional defensively, not because absence was observed: every response
     * captured from the dev API carried `items`, including the empty page past
     * the end of the results. Absent and empty would mean the same thing — no
     * rows matched — so a consumer reads it with `?? []` and cannot crash if a
     * future response omits it.
     */
    items?: TItem[];
    pageNumber: number;
    itemsPerPage: number;
    /** The size of the full filtered set, not of this page. */
    totalCount: number;
    asOf: string;
}

export interface MailProcessingSearchCriteria {
    /** Required. Omitting it is a 400, and it selects the row shape. */
    direction: MailDirection;
    mailboxID?: number;
    statuses?: ProcessingStatus[];
    /** The server-maintained definition of "failed" (spec §3.2). */
    failedOnly?: boolean;
    /** `lastFailureReason != null`. NOT a synonym for failedOnly (spec §3.3). */
    hasError?: boolean;
    /** Outbound only. Sending it on an inbound search is a 400. */
    origin?: MailOrigin;
    /** Filters `processingUpdatedAt`, NOT receipt time (spec §3.4). */
    dateFrom?: string;
    dateTo?: string;
    /** Matches subject, from and to. Not the body (spec §3.7). */
    text?: string;
    pageNumber: number;
    /** 1..200 inclusive. 201 is a 400, not a clamp. */
    itemsPerPage: number;
    /**
     * Only `"status"` is recognised. Anything else returns `200` and silently
     * sorts newest-first (spec §3.5), so a typo would be invisible at runtime
     * — which is the whole reason this is the literal type and not `string`.
     * Widen it only when the server learns another column.
     */
    sortColumn?: "status";
    sortAscending?: boolean;
}

/**
 * One page of emails for ONE tenant — the tenant named by the token.
 *
 * The token must be a tenant-scoped token from `getCachedTenantToken`, not the
 * sys-admin token the Core routes take. Passing the wrong one does not fail
 * loudly: it returns some other tenant's emails.
 *
 * The response is modelled as a discriminated union on the `direction` SENT,
 * not on anything in the payload — the caller knows which shape it asked for,
 * and `isInbound` narrows individual rows.
 *
 * `CallEmsApi` has no try/catch around its own `fetch` or `response.json()`,
 * so it rejects on a network error or a malformed body. This wrapper converts
 * that into a status-0 response so callers have one shape to handle.
 */
export async function SearchEmails(
    tenantScopedToken: string,
    criteria: MailProcessingSearchCriteria,
): Promise<iEmsApiResponseBody<Paged<EmailListItem>>> {
    try {
        return await CallEmsApi<Paged<EmailListItem>>(
            tenantScopedToken,
            "POST",
            "MailProcessing/Emails/Search",
            criteria,
        );
    } catch {
        return networkFailure<Paged<EmailListItem>>();
    }
}
